import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { Category } from '../../../Entities';
import { ImageLoadingSkeleton } from './ImageLoadingSkeleton';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';

type ImageCardProps = {
    imageReference: string;
    filename: string;
    category: number;
    isSelected?: boolean;
    isPortrait: boolean;
    content?: React.ReactNode;
    boldFilename?: boolean;
};

function getCategoryText(category: number): string {
    if (category === Category.NORMAL) {
        return Translate('order-page/edited-image/delivery');
    }

    if (category === Category.EXTRA) {
        return Translate('image-processing/page/list/thumbnail/caption/extra');
    }

    if (category === Category.EJLEV) {
        return Translate('image-processing/page/list/thumbnail/caption/no-delivery');
    }

    return '';
}

function getCategoryBackgroundColor(category: number): string {
    if (category === Category.NORMAL) {
        return '#3b873e';
    }

    if (category === Category.EXTRA) {
        return '#F57C00';
    }

    if (category === Category.EJLEV) {
        return '#d93934';
    }

    return '';
}

const CardContainer = styled('div')<{ isSelected: boolean }>(({ isSelected }) => ({
    width: '100%',
    aspectRatio: '4 / 3',
    padding: '4px',
    boxSizing: 'border-box',
    backgroundColor: isSelected ? '#3D9970' : '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'inherit',
    transition: 'background-color 0.15s ease'
}));

const ImageWrapper = styled('div')<{ isPortrait: boolean }>(({ isPortrait }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isPortrait ? '#ededed' : 'transparent',
    overflow: 'hidden'
}));

const CategoryBadge = styled('span')<{ category: number }>(({ category }) => ({
    position: 'absolute',
    top: '4px',
    left: '4px',
    fontSize: '10px',
    borderRadius: '4px',
    color: '#fff',
    backgroundColor: getCategoryBackgroundColor(category),
    padding: '2px 6px',
    zIndex: 1
}));

const StyledImage = styled('img')<{ isPortrait: boolean; loaded: boolean }>(
    ({ isPortrait, loaded }) => ({
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: isPortrait ? 'contain' : 'cover',
        opacity: loaded ? 1 : 0,
        transition: 'opacity 300ms ease-in'
    })
);

const FilenameOverlay = styled('span', {
    shouldForwardProp: (prop) => prop !== 'bold'
})<{ bold: boolean }>(({ bold }) => ({
    color: '#e7e7e7',
    backgroundColor: 'rgba(0,0,0,0.25)',
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '0.1125em 2.6125em 0.1125em 0.6125em',
    fontWeight: bold ? 700 : 'inherit'
}));

const ThumbnailSkeletonOverlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
});

export function ImageCard({
    imageReference,
    filename,
    category,
    isPortrait,
    isSelected = false,
    content,
    boldFilename = false
}: ImageCardProps) {
    let apiUrl = useApiUrl();
    const [loaded, setLoaded] = React.useState(false);
    let { logger } = useTelemetryContext();

    return (
        <CardContainer isSelected={isSelected}>
            <ImageWrapper isPortrait={isPortrait}>
                <CategoryBadge category={category}>{getCategoryText(category)}</CategoryBadge>
                {content}
                {!content && !loaded && (
                    <ThumbnailSkeletonOverlay>
                        <ImageLoadingSkeleton />
                    </ThumbnailSkeletonOverlay>
                )}
                {!content && imageReference ? (
                    <StyledImage
                        isPortrait={isPortrait}
                        loaded={loaded}
                        src={`${apiUrl}/file/${imageReference}`}
                        loading="lazy"
                        decoding="async"
                        onLoad={() => {
                            setLoaded(true);
                        }}
                        onError={(error) => {
                            logger.logException(error, {
                                origin: `ImageCard.onError for ${filename}, imageReference: ${imageReference}`
                            });
                        }}
                    />
                ) : null}
                <FilenameOverlay bold={boldFilename}>{filename}</FilenameOverlay>
            </ImageWrapper>
        </CardContainer>
    );
}
